<template>
  <div class="p-6">
    <!-- Title -->
    <div class="mb-5">
      <h2 class="text-lg font-bold">
        {{ isDraftOnly ? $t('W: Kreditér (fortryd kladde)') : $t('W: Kreditér faktura') }}
        <span v-if="invoiceNumber">#{{ invoiceNumber }}</span>
      </h2>
      <p
        v-if="isDraftOnly"
        class="mt-1 text-sm">
        {{
          $t(
            'W: Fakturaen blev kun oprettet som kladde. Business Central bliver spurgt om, hvor den er nu, så du kan se præcis, hvad der sker.'
          )
        }}
      </p>
      <p
        v-else
        class="mt-1 text-sm">
        {{
          $t(
            'W: Hele fakturaen krediteres i Business Central. Der kan ikke krediteres et delbeløb.'
          )
        }}
      </p>
    </div>

    <!-- A draft-only invoice: what happens depends on where Business Central has it right now -->
    <div
      v-if="isDraftOnly && draftOnlyState === 'unknown'"
      class="border-feedback-error mb-5 rounded-lg border px-4 py-3 text-sm">
      <span class="text-feedback-error font-semibold">
        {{ $t('W: Fakturaens tilstand kunne ikke læses i Business Central') }}
      </span>
      <p class="mt-1">
        {{ $t('W: Intet er ændret. Prøv igen om lidt.') }}
      </p>
    </div>
    <div
      v-else-if="isDraftOnly"
      class="mb-5 rounded-lg border border-border-subtle px-4 py-3 text-sm">
      <span class="mb-2 block text-xs font-semibold tracking-wider uppercase">
        {{ $t('W: Det sker der') }}
      </span>
      <ul class="list-disc space-y-1 pl-4">
        <li v-if="draftOnlyState === 'draft'">
          {{ $t('W: Fakturaen er stadig en kladde i Business Central — kladden slettes') }}
        </li>
        <li v-else-if="draftOnlyState === 'posted'">
          {{
            $t(
              'W: Fakturaen er bogført i Business Central. Der oprettes en kreditnota som kladde — den bogføres ikke herfra'
            )
          }}
        </li>
        <li v-else>
          {{ $t('W: Kladden findes ikke længere i Business Central — der er intet at slette') }}
        </li>
        <li>{{ $t('W: Ordren sættes tilbage til Klar til fakturering, så betaler og priser kan rettes') }}</li>
        <li>{{ $t('W: Automatisk fakturering slås fra, så ordren ikke sendes af sig selv igen') }}</li>
      </ul>
    </div>

    <!-- What happens -->
    <div
      v-else
      class="mb-5 rounded-lg border border-border-subtle px-4 py-3 text-sm">
      <span class="mb-2 block text-xs font-semibold tracking-wider uppercase">
        {{ $t('W: Det sker der') }}
      </span>
      <ul class="list-disc space-y-1 pl-4">
        <li>{{ $t('W: Der oprettes og bogføres en korrektiv kreditnota i Business Central') }}</li>
        <li>{{ $t('W: «Opret som kladde» opretter kun kreditnotaen — den bogføres senere, og ordren er lukket, indtil det sker') }}</li>
        <li>
          {{
            isPostedOnly
              ? $t('W: Kreditnotaen bogføres i Business Central — afsendelse klarer I selv dér')
              : $t('W: Kreditnotaen sendes til kunden af Business Central')
          }}
        </li>
        <li>{{ $t('W: Ordren sættes tilbage til Klar til fakturering, så betaler og priser kan rettes') }}</li>
        <li>{{ $t('W: Automatisk fakturering slås fra, så ordren ikke sendes af sig selv igen') }}</li>
      </ul>
    </div>

    <!-- An invoice that covers several orders — a consolidated invoice from before they were removed
         (2026-10-15). BC credits the whole posted invoice, so every order on it is reopened. -->
    <div
      v-if="isConsolidated"
      class="border-feedback-error mb-5 rounded-lg border px-4 py-3">
      <span class="text-feedback-error text-sm font-semibold">
        {{ $t('W: Fakturaen dækker {count} ordrer', { count: coveredOrderCount }) }}
      </span>
      <p class="mt-1 text-sm">
        {{
          $t(
            'W: Business Central krediterer hele den bogførte faktura, så alle {count} ordrer krediteres og åbnes for fakturering igen — ikke kun denne.',
            { count: coveredOrderCount }
          )
        }}
      </p>
    </div>

    <!-- Reason -->
    <div class="mb-5">
      <label
        for="credit-invoice-reason"
        class="mb-1.5 block text-xs font-semibold tracking-wider uppercase">
        {{ $t('W: Årsag') }}
      </label>
      <textarea
        id="credit-invoice-reason"
        v-model="reason"
        rows="3"
        :placeholder="$t('W: f.eks. Forkert betaler, forkert pris på analyse...')"
        class="w-full resize-none rounded-md border border-border-subtle px-3 py-2 text-sm focus:border-border-strong focus:outline-none" />
      <span class="mt-1 block text-xs">
        {{ reasonHint }}
      </span>
    </div>

    <!-- Buttons -->
    <div
      v-if="isDraftOnly"
      class="flex justify-end gap-3">
      <UiBaseButton
        :text="$t('W: Annuller')"
        theme="neutral"
        @button-click="$emit('close')" />
      <UiBaseButton
        :text="draftOnlyConfirmText"
        :isDisabled="isSubmitDisabled"
        :isLoading="isCrediting"
        @button-click="onSubmit('post')" />
    </div>
    <div
      v-else
      class="flex justify-end gap-3">
      <UiBaseButton
        :text="$t('W: Fortryd')"
        theme="neutral"
        @button-click="$emit('close')" />
      <UiBaseButton
        :text="$t('W: Opret som kladde')"
        theme="neutral"
        :isDisabled="isSubmitDisabled"
        :isLoading="isCrediting && submittedMode === 'draft'"
        @button-click="onSubmit('draft')" />
      <UiBaseButton
        :text="isPostedOnly ? $t('W: Kreditér') : $t('W: Kreditér og send kreditnota')"
        :isDisabled="isSubmitDisabled"
        :isLoading="isCrediting && submittedMode === 'post'"
        @button-click="onSubmit('post')" />
    </div>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { useModalStore } from '@/stores/modalStore'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'

export default {
  name: 'CreditInvoiceModal',

  emits: ['close'],

  props: {
    invoiceNumber: { type: String, default: '' },
    /** How many orders the invoice covers — more than one only for a consolidated invoice made
     *  before they were removed; the opening view passes `invoice.orderIDs.length`. */
    coveredOrderCount: { type: Number, default: 1 },
    /** The lab runs with the invoice complete on posting: the credit memo is only posted, and the lab
     *  sends (or not) from Business Central. */
    isPostedOnly: { type: Boolean, default: false },
    /** Set only when crediting a draft-only invoice: what Business Central holds for it right now —
     *  `draft` (it is deleted), `posted` (a credit memo draft is created), `gone` (nothing to delete) or
     *  `unknown` (could not be read: nothing can be done). Changes the wording and the buttons. */
    draftOnlyState: {
      type: String as PropType<'draft' | 'posted' | 'gone' | 'unknown' | null>,
      default: null,
    },
    onConfirm: {
      type: Function as PropType<(reason: string, postingMode: 'draft' | 'post') => void | Promise<void>>,
      required: true,
    },
  },

  data() {
    return {
      unsavedGuard: useUnsavedChangesGuard(),
      modalEntryID: useModalStore().top?.id,

      reason: '',
      isCrediting: false,
      /** Which button was pressed, so only that one shows the spinner. */
      submittedMode: 'post' as 'draft' | 'post',
    }
  },

  computed: {
    isConsolidated(): boolean {
      return this.coveredOrderCount > 1
    },

    isDraftOnly(): boolean {
      return this.draftOnlyState !== null
    },

    /** The one confirm button of a draft-only invoice — it names what it will do. */
    draftOnlyConfirmText(): string {
      if (this.draftOnlyState === 'posted') return this.$t('W: Opret kreditnota-kladde')
      if (this.draftOnlyState === 'gone') return this.$t('W: Åbn ordren igen')
      return this.$t('W: Slet kladde og åbn ordren')
    },

    /** Says where the reason ends up — it must match what the backend stores. A draft that is deleted
     *  (or already gone) takes its invoice record with it, so its reason lives in the log only; every
     *  other case keeps the invoice, with the reason on it, listed under "Tidligere fakturaer". */
    reasonHint(): string {
      return this.draftOnlyState === 'draft' || this.draftOnlyState === 'gone'
        ? this.$t('W: Årsagen gemmes i loggen')
        : this.$t('W: Årsagen gemmes på fakturaen og vises under Tidligere fakturaer')
    },

    // Never optional: the reason is what documents that the credit was authorised.
    isSubmitDisabled(): boolean {
      return this.isCrediting || this.draftOnlyState === 'unknown' || this.reason.trim().length === 0
    },
  },

  methods: {
    async onSubmit(postingMode: 'draft' | 'post'): Promise<void> {
      if (this.isSubmitDisabled) return
      this.submittedMode = postingMode
      this.isCrediting = true
      try {
        // The handler does its own error handling (toast + inline error on the view) and never
        // rethrows — awaiting it here is only for the loading state and closing afterwards.
        await this.onConfirm(this.reason.trim(), postingMode)
      } finally {
        this.isCrediting = false
      }
      this.$emit('close')
    },
  },

  created() {
    // A typed credit reason.
    this.unsavedGuard.register({
      snapshot: () => ({
        reason: this.reason,
      }),
      modalId: this.modalEntryID,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>
